import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatIST } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Cog, RefreshCw, Factory } from 'lucide-react';

import { Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid } from '@/components/data';

export default function SupervisorProductionQueuePage() {
  const navigate = useNavigate();
  const [stagesData, setStagesData] = useState([]);
  const [machinesView, setMachinesView] = useState(null);
  const [selectedStage, setSelectedStage] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [stagesRes, machinesRes] = await Promise.all([
        api.get('/supervisor/stages'),
        api.get(`/supervisor/machines-view${selectedStage ? `?stage_name=${encodeURIComponent(selectedStage)}` : ''}`),
      ]);
      setStagesData(stagesRes.data.stages || []);
      setMachinesView(machinesRes.data);
    } catch (e) {
      toast.error('Failed to load production queue');
    }
    setLoading(false);
  }, [selectedStage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  const mvStages = machinesView?.stages || [];

  // Every queued card as one flat list — the stage and the machine it is on are
  // columns, which is what lets you see the whole floor's queue in one place
  // rather than expanding eight stage cards to find the one that is backed up.
  const rows = [];
  for (const stage of stagesData) {
    for (const card of stage.job_cards || []) {
      rows.push({ ...card, __stage: stage.stage_name, __rowId: card.row_key });
    }
  }

  const machines = machinesView?.machines || [];
  const unassigned = rows.filter(r => !r.machine_name).length;

  const columns = [
    {
      key: 'item',
      header: 'Item / Customer',
      width: 'minmax(13rem,1.5fr)',
      render: (card) => (
        <>
          <span className="block truncate font-semibold text-slate-900">
            {card.item_name || card.customer_name}
          </span>
          <span className="block truncate text-[11px] leading-tight text-slate-500">
            {card.firm_name || card.customer_name} · {card.order_id}
          </span>
        </>
      ),
    },
    {
      key: '__stage',
      header: 'Stage',
      width: '10rem',
      render: (card) => (
        <span className="block truncate text-[12px] font-medium text-brand-blue">{card.__stage}</span>
      ),
    },
    {
      key: 'machine_name',
      header: 'Machine',
      width: '9.5rem',
      render: (card) => card.machine_name ? (
        <span className="flex min-w-0 items-center gap-1 text-[12px] text-slate-600">
          <Cog className="h-3 w-3 shrink-0 text-slate-400" />
          <span className="truncate">{card.machine_name}</span>
        </span>
      ) : (
        <span className="text-[11px] font-medium text-amber-600">Unassigned</span>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      width: '5.5rem',
      render: (card) => <span className="font-medium">{card.quantity?.toLocaleString() || '—'}</span>,
      total: (list) => list.reduce((s, c) => s + (c.quantity || 0), 0).toLocaleString(),
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '5.5rem',
      mobile: false,
      render: (card) => (
        <span className={`text-[11px] font-medium ${
          card.priority === 'high' ? 'text-red-600'
            : card.priority === 'low' ? 'text-emerald-600' : 'text-amber-600'
        }`}>{card.priority || '—'}</span>
      ),
    },
    {
      key: 'delivery_date',
      header: 'Due',
      width: '6.5rem',
      mobile: false,
      render: (card) => (
        <span className="whitespace-nowrap text-[11px] text-slate-600">
          {card.delivery_date ? formatIST(card.delivery_date, { dateOnly: true }) : '—'}
        </span>
      ),
    },
  ];

  return (
    <Screen data-testid="supervisor-production-queue-page">
      <CommandBar
        title="Production Queue"
        subtitle="Job cards by stage and machine"
        actions={[{ key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchData }]}
      />

      <StatStrip
        stats={[
          { key: null, label: 'cards queued', value: rows.length, tone: 'blue' },
          { key: null, label: 'unassigned', value: unassigned, tone: unassigned ? 'warn' : 'good' },
          { key: null, label: 'stages busy', value: stagesData.filter(s => s.job_cards?.length).length },
          { key: null, label: 'machines loaded', value: machines.filter(m => m.job_cards?.length).length },
        ]}
      >
        <Select value={selectedStage || 'all'} onValueChange={(v) => setSelectedStage(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder="All stages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All stages</SelectItem>
            {stagesData.map(s => (
              <SelectItem key={s.stage_name} value={s.stage_name} className="text-xs">
                {s.stage_name} ({s.job_cards?.length || 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </StatStrip>

      <ScreenSplit
        railWidth="18rem"
        rail={
          <Pane title="Machines" aside={`${machines.length}`} className="max-h-full">
            {machines.length === 0 ? (
              <p className="p-4 text-center text-xs text-slate-400">No machines with active jobs</p>
            ) : machines.map(machine => (
              <div key={machine.machine_id || machine.machine_name}
                className="border-b border-slate-100 px-2.5 py-1.5 last:border-b-0">
                <span className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <Cog className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <span className="truncate text-[13px] font-medium text-slate-700">{machine.machine_name}</span>
                  </span>
                  <span className={`shrink-0 text-[11px] font-semibold tabular-nums ${
                    machine.job_cards?.length ? 'text-brand-blue' : 'text-slate-400'
                  }`}>
                    {machine.job_cards?.length || 0}
                  </span>
                </span>
                {machine.job_cards?.slice(0, 3).map(c => (
                  <button
                    key={c.line_item_id}
                    type="button"
                    onClick={() => navigate(`/supervisor/job-card/${c.line_item_id}`)}
                    className="mt-0.5 block w-full truncate rounded px-1 py-0.5 text-left text-[10px] text-slate-500 hover:bg-slate-100"
                  >
                    {c.item_name || c.customer_name} · {c.quantity} pcs
                  </button>
                ))}
                {(machine.job_cards?.length || 0) > 3 && (
                  <span className="mt-0.5 block px-1 text-[10px] text-slate-400">
                    +{machine.job_cards.length - 3} more
                  </span>
                )}
              </div>
            ))}
          </Pane>
        }
      >
        <Pane
          title="Queue"
          aside={selectedStage || 'all stages'}
          scroll={false}
          bodyClassName="flex flex-col"
        >
          <DataGrid
            data-testid="supervisor-queue-table"
            columns={columns}
            rows={rows}
            getRowId={(card) => card.__rowId}
            onRowClick={(card) => navigate(`/supervisor/job-card/${card.line_item_id}`)}
            showTotals={rows.length > 0}
            empty={
              <span className="flex flex-col items-center gap-2 text-slate-400">
                <Factory className="h-8 w-8 text-slate-300" />
                Nothing queued right now
              </span>
            }
          />
        </Pane>
      </ScreenSplit>
    </Screen>
  );
}
